import React from 'react';
import KaAlert from '../Common/KaAlert'
import { FormCheckbox } from '../Common/FormCheckbox';
import SpinnerButton from '../Common/SpinnerButton';
import { useSelectedBusiness } from '../UserAccess/useSelectedBusiness';
import KaModal from '../Common/KaModal';
import useUserRequest, { UserRequestSpec } from '../Common/useUserRequest';
import { HttpMethod } from '../Common/useFetch';
import useDisplayMessage from '../Common/useDisplayMessage';
import { BusinessUserUpsertDto, UserRoleDto } from '../Requests/useGetUsers';
import { Path, useForm } from 'react-hook-form';
import { RowFormText } from '../Common/Forms/FormText';
import { RowFormSelectSearch } from '../Common/Forms/SelectSearch';
import { toRoleSelectOptions, UserRoleNames } from './userRoles';

interface IProps {
  roles: UserRoleDto[];
  visible: boolean;
  onHide: () => void;
  onSuccess: (businessUser: BusinessUserUpsertDto) => void;
}

const postSpec = (businessId: string): UserRequestSpec => ({
  path: `/api/businesses/${businessId}/users`,
  method: HttpMethod.POST,
});

const defaultRole = (roles: UserRoleDto[]) =>
  roles.find(r => r.name === UserRoleNames.standard) ?? roles[0];

export const AddUser = (props: IProps) => {
  const business = useSelectedBusiness();
  const requestSpec = postSpec(business.id);
  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, getValues, setValue, formState: { errors } } = useForm<BusinessUserUpsertDto>({
    defaultValues: { emailAddress: '', userRoleId: defaultRole(props.roles)?.id, addAsErrorRecipient: false }
  });

  const handleCreateUpdate = (businessUpsert: BusinessUserUpsertDto) => {
    useAddUser.request(businessUpsert);
  }

  const useAddUser = useUserRequest<BusinessUserUpsertDto>(requestSpec, {
    onSuccess: () => props.onSuccess(getValues()),
    onError: (e) => displayMessage.fail(e.message),
  });

  const updateAddAsErrorRecipient = (e: React.ChangeEvent<HTMLInputElement>) =>
    setValue("addAsErrorRecipient", e.target.checked);

  const hookProps = (name: Path<BusinessUserUpsertDto>) => ({
    labelSpace: 3,
    inputSpace: 8,
    name: name,
    setValue,
    register,
    errors,
  })

  const onClose = () => {
    props.onHide();
    displayMessage.clear();
  }

  return (<KaModal
    onHide={onClose}
    show={props.visible}
    title={'Add user to business'}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText displayName="User Email"
        {...hookProps("emailAddress")}
        options={{
          required: 'Email Address is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Email Address is invalid'
          }
        }}
      />
      <RowFormSelectSearch displayName="Role"
        id="userRoleId"
        {...hookProps("userRoleId")}
        selectedValue={defaultRole(props.roles)?.id}
        hideSearch={true}
        selectOptions={toRoleSelectOptions(props.roles)} />
      <FormCheckbox label="Subscribe to connector error emails" onChange={updateAddAsErrorRecipient} offset={3} />
    </>)}
    footer={(
      <SpinnerButton
        className="btn-ghost-primary"
        spinning={useAddUser.isLoading}
        onClick={handleSubmit(handleCreateUpdate)}>
        Add
      </SpinnerButton>)
    }
  />);
}
